import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, MapPin, Send, LifeBuoy } from "lucide-react";
import { Reveal } from "../Reveal";
import { api, formatApiErrorDetail } from "../../lib/api";

const TOPICS = ["General enquiry", "Request a demo", "Register my society", "Support"];

export const ContactSection = () => {
  const [form, setForm] = useState({ topic: "General enquiry", name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        message: `[${form.topic}] ${form.message}`,
      });
      toast.success("Message sent — our team will reach out shortly.");
      setForm({ topic: "General enquiry", name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-slate-100 bg-slate-50/60 py-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="eyebrow mb-4">Contact Us</p>
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Talk to the SynqSMC team
            </h2>
            <p className="mt-4 max-w-md text-slate-600">
              Questions, a product demo, or onboarding your society — send us a note and we will
              get back within one business day.
            </p>
            <div className="mt-10 space-y-5">
              <a href="mailto:support@synqsmc.com" data-testid="contact-support-link" className="flex items-center gap-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <LifeBuoy size={16} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-slate-800">Support</span>
                  <span className="block text-sm text-slate-500">support@synqsmc.com</span>
                </span>
              </a>
              <a href="mailto:office@synqsmc.com" data-testid="contact-email-link" className="flex items-center gap-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <Mail size={16} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-slate-800">Sales & demos</span>
                  <span className="block text-sm text-slate-500">office@synqsmc.com</span>
                </span>
              </a>
              <div className="flex items-center gap-4" data-testid="contact-address">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <MapPin size={16} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-slate-800">Office</span>
                  <span className="block text-sm text-slate-500">Mumbai, India</span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="card-soft p-7 shadow-lg shadow-slate-200/50 sm:p-9">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="contact-topic" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    I want to
                  </label>
                  <select id="contact-topic" data-testid="contact-topic-select" className="input-field" value={form.topic} onChange={set("topic")}>
                    {TOPICS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-name" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Full name</label>
                  <input id="contact-name" data-testid="contact-name-input" className="input-field" placeholder="Aarav Mehta" value={form.name} onChange={set("name")} required />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Email</label>
                  <input id="contact-email" type="email" data-testid="contact-email-input" className="input-field" placeholder="you@example.com" value={form.email} onChange={set("email")} required />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-phone" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Phone <span className="text-slate-400">(optional)</span>
                  </label>
                  <input id="contact-phone" data-testid="contact-phone-input" className="input-field" placeholder="+91 …" value={form.phone} onChange={set("phone")} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Message</label>
                  <textarea id="contact-message" rows={4} data-testid="contact-message-input" className="input-field resize-none" placeholder="Tell us about your society…" value={form.message} onChange={set("message")} required minLength={5} />
                </div>
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="contact-submit-button"
                className="btn-primary mt-7 w-full !py-3.5 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {busy ? "Sending…" : "Send message"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
